// src/client/renderer/vfx/items/cursed-relic.ts
//
// Cursed Relic cast pool.
//
// Triggered by Cursed Relic item_cast. The persistent victim aura is owned by
// the rig-attached character status effect manager.
//
//   1. ItemCastEvent (itemId='cursed_relic') → phase='cast':
//        Caster aura swirl anticipation (700ms, scale 0→1.4→0, opacity 1→0).
//        Pool: cast aura one-shot max-active=2.
//
//   2. ItemDotAppliedEvent (itemId='cursed_relic'):
//        The victim plume is owned by the rig-attached character status effect
//        manager; this world-space item VFX pool does not spawn it.
//
//   3. ItemDotTickEvent (itemId='cursed_relic'):
//        No-op; Cursed Relic no longer has per-turn tick VFX.
//
//   4. ItemDotExpiredEvent (itemId='cursed_relic'):
//        Rig-attached plume cleanup is handled by character/status-effects.ts.
//
// casterId field overloaded to carry victimId:
//   ItemVfxSpawnArgs has no `victimId` field. Following shadow-stalker /
//   mystery-box convention, resolver places `event.victimId` into
//   `args.casterId`. Documented in JSDoc on the resolver.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Follow-up lockout events
//   are intentionally silent here.
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry
//   once per frame across all pools.
//
// F4 dispose iterates active cast emitters.
// F5 acquire-after-dispose returns null (closure `disposed` flag).

import * as THREE from 'three';
import type { GameEvent, ItemCastEvent } from '../../../../shared/game/types';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import { disposeSkillshotGroup } from './skillshot-shared';
import { getItemVfxSmokeTexture, ITEM_VFX_SMOKE_ALPHA_TEST } from './sprite-textures';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Cast aura max active slots. */
const MAX_CAST_ACTIVE = 2;

/** Cast aura anticipation duration (700ms). */
const CAST_DURATION = 0.7;

/** Cursed Relic purple-black aura colour. */
const AURA_COLOUR = 0x9933cc;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state interfaces
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the caster aura swirl for cast phase.
 * Scale 0→1.4→0, opacity 1→0 over 700ms.
 */
function buildCastAuraEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();
  group.userData.cursedRelicSigil = true;
  group.position.set(anchor.x, anchor.y + 1.1, anchor.z);

  const sigilMat = new THREE.LineBasicMaterial({
    color: 0xb46cff,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    linewidth: 2,
  });
  const shadowMat = new THREE.LineBasicMaterial({
    color: 0x12001e,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    linewidth: 2,
  });

  function makeStroke(points: THREE.Vector3[], material: THREE.LineBasicMaterial) {
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, material);
    line.userData.cursedRelicStroke = true;
    return line;
  }

  function makeArc(radius: number, start: number, end: number, material: THREE.LineBasicMaterial) {
    const points: THREE.Vector3[] = [];
    const segments = 18;
    for (let i = 0; i <= segments; i++) {
      const t = start + ((end - start) * i) / segments;
      points.push(new THREE.Vector3(Math.cos(t) * radius, Math.sin(t) * radius, 0));
    }
    return makeStroke(points, material);
  }

  const arcs = [
    makeArc(0.42, -0.2, Math.PI * 0.92, sigilMat),
    makeArc(0.3, Math.PI * 0.55, Math.PI * 1.55, shadowMat),
    makeArc(0.18, Math.PI * 1.1, Math.PI * 2.0, sigilMat),
  ];
  for (const arc of arcs) group.add(arc);

  const rune = makeStroke(
    [
      new THREE.Vector3(-0.22, 0.2, 0),
      new THREE.Vector3(0, -0.24, 0),
      new THREE.Vector3(0.24, 0.16, 0),
      new THREE.Vector3(-0.12, -0.02, 0),
    ],
    sigilMat,
  );
  group.add(rune);

  const smokeMat = new THREE.SpriteMaterial({
    map: getItemVfxSmokeTexture(),
    color: AURA_COLOUR,
    transparent: true,
    opacity: 0.52,
    alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const smoke = new THREE.Sprite(smokeMat);
  smoke.scale.set(0.9, 0.9, 1);
  smoke.position.z = -0.03;
  group.add(smoke);
  group.scale.set(0, 0, 0);

  const handle = registry.create('cursed_relic_cast');
  state.handle = handle;
  const tl = handle.timeline;

  // Scale: 0 → 1.4 over first half
  tl.to(
    group.scale,
    {
      x: 1.4,
      y: 1.4,
      z: 1,
      duration: CAST_DURATION * 0.5,
      ease: 'power2.out',
    },
    0,
  );

  // Scale: 1.4 → 0 over second half (swirl collapses)
  tl.to(
    group.scale,
    {
      x: 0,
      y: 0,
      z: 0,
      duration: CAST_DURATION * 0.5,
      ease: 'power1.in',
    },
    CAST_DURATION * 0.5,
  );

  // Opacity 1 → 0 over entire duration
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: CAST_DURATION,
      ease: 'power1.in',
      onUpdate: () => {
        sigilMat.opacity = opProxy.value;
        shadowMat.opacity = opProxy.value * 0.8;
        smokeMat.opacity = opProxy.value * 0.52;
      },
    },
    0,
  );
  tl.to(
    group.rotation,
    {
      z: Math.PI * 0.35,
      duration: CAST_DURATION,
      ease: 'power1.out',
    },
    0,
  );

  // Mark finished at end
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    CAST_DURATION,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createCursedRelicPool
// ─────────────────────────────────────────────────────────────────────────────

export function createCursedRelicPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /** phase='cast' — one-shot, max-active=2. */
  const castStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase } = args;

    // ── Cast phase: caster aura swirl ─────────────────────────────────────
    if (phase === 'cast') {
      if (castStates.size >= MAX_CAST_ACTIVE) return null;
      const anchor = args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildCastAuraEmitter(anchor, deps, state);
      scene.add(group);
      castStates.set(group, state);
      return group;
    }

    // ── Active curse aura is rig-attached by character/status-effects.ts.
    if (phase === 'active') {
      return null;
    }

    // Cursed Relic no longer emits per-turn tick VFX.
    if (phase === 'tick') {
      return null;
    }

    // ── Expire curse aura is rig-attached by character/status-effects.ts.
    if (phase === 'expire') {
      return null;
    }

    return null;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const emitter of castStates.keys()) all.add(emitter);
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const castState = castStates.get(emitter);
    if (castState) return castState.finished;

    // Unknown emitter
    return true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    // Check cast pool
    const castState = castStates.get(emitter);
    if (castState) {
      castState.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      castStates.delete(emitter);
      return;
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────
  // Iterates all active sets/maps. Idempotent (closure flag `disposed`).

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // 1. Cast aura active set
    for (const [emitter, state] of castStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    castStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveCursedRelicForEvent
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolver for Cursed Relic VFX events.
 *
 * @param event                - The game event to resolve.
 * @param getPositionForPlayer - Optional: playerId → world position.
 * @param getPositionForVictim - Optional: victimId → world position (same type
 *   as getPositionForPlayer — resolver passes victimId to the callback).
 */
export function resolveCursedRelicForEvent(
  event: GameEvent,
  getPositionForPlayer?: (id: string) => { x: number; y: number; z: number } | undefined,
  getPositionForVictim?: (id: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'cursed_relic'; args: ItemVfxSpawnArgs } | null {
  void getPositionForVictim;
  // ── item_cast with itemId='cursed_relic' → cast aura swirl ───────────────
  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    if (cast.itemId !== 'cursed_relic') return null;
    return {
      poolId: 'cursed_relic',
      args: {
        phase: 'cast',
        casterId: cast.casterId,
        casterPosition: getPositionForPlayer?.(cast.casterId),
      },
    };
  }

  // item_dot_applied is handled by the rig-attached character status effect manager.
  if (event.type === 'item_dot_applied') {
    return null;
  }

  // Cursed Relic no longer has per-turn tick VFX.
  if (event.type === 'item_dot_tick') {
    return null;
  }

  // item_dot_expired is handled by the rig-attached character status effect manager.
  if (event.type === 'item_dot_expired') {
    return null;
  }

  return null;
}
